import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import styles from "./css/PaymentPage.module.css";
import { buildApiUrl, buildSiteUrl } from "../config/runtime";
import { usePaymentCatalog, useSiteContent } from "../hooks/useSiteContent";
import MetaManager from "./MetaManager";

function isExternal(url) {
  return /^https?:\/\//i.test(String(url || "").trim());
}

function groupProducts(products) {
  const order = ["helper", "master", "max", "other"];
  return order
    .map((planKey) => ({
      planKey,
      items: products.filter((item) => item.planKey === planKey)
    }))
    .filter((group) => group.items.length > 0);
}

export default function PaymentPage() {
  const location = useLocation();
  const { content } = useSiteContent();
  const { products, loading: catalogLoading, error: catalogError } = usePaymentCatalog();
  const [userId, setUserId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("yookassa");
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");

  const paymentContent = content?.payment || {};
  const groupedProducts = groupProducts(products);
  const selectedProduct = products.find((product) => String(product.id) === String(selectedProductId)) || null;

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const idFromQuery = String(query.get("id") || "").trim();
    if (idFromQuery) {
      setUserId(idFromQuery);
    }
  }, [location.search]);

  useEffect(() => {
    if (!products.length) return;

    const query = new URLSearchParams(location.search);
    const planFromQuery = String(query.get("plan") || "").trim().toLowerCase();
    const selectedExists = products.some((product) => String(product.id) === String(selectedProductId));

    if (selectedExists) return;

    const byId = products.find((product) => String(product.id) === planFromQuery);
    const byPlanKey = products.find((product) => String(product.planKey) === planFromQuery);
    const nextProduct = byId || byPlanKey || products[0];

    if (nextProduct) {
      setSelectedProductId(String(nextProduct.id));
    }
  }, [location.search, products, selectedProductId]);

  useEffect(() => {
    if (!selectedProduct) return;
    const methods = Array.isArray(selectedProduct.methods) ? selectedProduct.methods : [];
    if (!methods.includes(paymentMethod)) {
      setPaymentMethod(methods[0] || "yookassa");
    }
  }, [paymentMethod, selectedProduct]);

  async function handlePayment() {
    if (!userId.trim()) {
      setLocalError("Укажите VK ID или ссылку на профиль.");
      return;
    }

    if (!selectedProduct) {
      setLocalError("Выберите тариф.");
      return;
    }

    setSubmitting(true);
    setLocalError("");

    try {
      const response = await axios.post(buildApiUrl("/chat/site/payment-link"), {
        userId: userId.trim(),
        tariffId: selectedProduct.id,
        method: paymentMethod
      });

      const redirectUrl = String(response.data?.url || "").trim();
      if (!redirectUrl) {
        throw new Error("empty redirect url");
      }

      window.location.href = redirectUrl;
    } catch (err) {
      setLocalError(err.response?.data?.error || "Не удалось сформировать ссылку на оплату.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <MetaManager
        seo={content?.seo?.payment}
        fallbackTitle="Оплата Сверхум — выберите тариф для педагога"
        fallbackDescription="Выберите подходящий тариф Сверхум и оплатите его удобным способом."
        fallbackPath="/payment"
      />

      <div className={styles.shell}>
        <header className={styles.topbar}>
          <div className={styles.brand}>
            <span className={styles.brandMark} />
            <span>Сверхум</span>
          </div>
          <a className={styles.homeLink} href={buildSiteUrl("/")}>
            На главную
          </a>
        </header>

        <div className={styles.layout}>
          <section className={styles.hero}>
            <p className={styles.eyebrow}>Оплата и доступ</p>
            <h1>{paymentContent.headline}</h1>
            <p>{paymentContent.subheadline}</p>

            <div className={styles.statusRow}>
              <span className={styles.statusChip}>Актуальный каталог из backend</span>
              <span className={styles.statusChip}>Безопасная оплата через YooKassa</span>
              <span className={styles.statusChip}>VK Donut только для поддерживаемых тарифов</span>
            </div>

            <div className={styles.catalogSection}>
              <div className={styles.catalogHeader}>
                <div>
                  <h2>Тарифы</h2>
                  <p className={styles.catalogHint}>Выберите период и продукт, который нужен именно сейчас.</p>
                </div>
                {catalogLoading ? <span className={styles.catalogHint}>Обновляем каталог…</span> : null}
              </div>

              {catalogError ? (
                <div className={styles.error}>Не удалось загрузить каталог тарифов. Попробуйте обновить страницу.</div>
              ) : null}

              {groupedProducts.map((group) => (
                <div key={group.planKey} className={styles.planGroup}>
                  <div className={styles.planTitleRow}>
                    <div className={styles.planTitle}>{group.items[0]?.planTitle || "Тариф"}</div>
                    <span className={styles.planBadge}>{group.items.length} вариантов</span>
                  </div>
                  <div className={styles.productGrid}>
                    {group.items.map((product) => {
                      const selected = String(product.id) === String(selectedProductId);
                      return (
                        <button
                          key={product.id}
                          type="button"
                          className={`${styles.productCard} ${selected ? styles.productCardSelected : ""}`}
                          onClick={() => setSelectedProductId(String(product.id))}
                        >
                          <div className={styles.productMeta}>{product.periodLabel}</div>
                          <div className={styles.productPrice}>{product.price.toLocaleString("ru-RU")} ₽</div>
                          <div>{product.title}</div>
                          <div className={styles.productMethods}>{product.methods.join(" · ")}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <aside className={styles.panel}>
            <div>
              <h2>Оформление доступа</h2>
              <p className={styles.panelText}>{paymentContent.helpText}</p>
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.label} htmlFor="payment-user-id">
                VK ID или ссылка
              </label>
              <input
                id="payment-user-id"
                className={styles.input}
                placeholder="Например: id123456789 или https://vk.com/username"
                value={userId}
                onChange={(event) => setUserId(event.target.value)}
              />
            </div>

            <div className={styles.infoCard}>
              <h3>Выбранный тариф</h3>
              {selectedProduct ? (
                <>
                  <p className={styles.panelText}>
                    {selectedProduct.title} · {selectedProduct.periodLabel}
                  </p>
                  <div className={styles.productPrice}>{selectedProduct.price.toLocaleString("ru-RU")} ₽</div>
                </>
              ) : (
                <div className={styles.loading}>Выберите тариф из каталога слева.</div>
              )}
            </div>

            <div className={styles.fieldGroup}>
              <span className={styles.label}>Способ оплаты</span>
              <div className={styles.methodRow}>
                {(selectedProduct?.methods || []).map((method) => (
                  <button
                    key={method}
                    type="button"
                    className={`${styles.methodButton} ${paymentMethod === method ? styles.methodButtonActive : ""}`}
                    onClick={() => setPaymentMethod(method)}
                  >
                    {method === "vkdonut" ? "VK Donut" : "YooKassa"}
                  </button>
                ))}
              </div>
            </div>

            {localError ? <div className={styles.error}>{localError}</div> : null}

            <button className={styles.actionButton} onClick={handlePayment} disabled={!selectedProduct || submitting}>
              {submitting ? "Формируем ссылку…" : "Перейти к оплате"}
            </button>

            {paymentContent.agreementUrl ? (
              <a className={styles.textLink} href={paymentContent.agreementUrl} target="_blank" rel="noreferrer">
                Пользовательское соглашение
              </a>
            ) : null}

            {paymentContent.cancelUrl ? (
              <a className={styles.ghostButton} href={paymentContent.cancelUrl} target="_blank" rel="noreferrer">
                Отключить автопродление
              </a>
            ) : null}

            <div>
              <h3>Контакты</h3>
              <div className={styles.supportList}>
                {(paymentContent.contacts || []).map((item) => (
                  <div key={`${item.label}-${item.value}`} className={styles.supportItem}>
                    <span>{item.label}</span>
                    {item.url ? (
                      <a href={item.url} target={isExternal(item.url) ? "_blank" : undefined} rel="noreferrer">
                        {item.value || item.url}
                      </a>
                    ) : (
                      <strong>{item.value}</strong>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3>После оплаты</h3>
              <div className={styles.linkList}>
                {(paymentContent.successLinks || []).map((item) => (
                  <div key={`${item.label}-${item.url}`} className={styles.linkItem}>
                    <span>{item.label}</span>
                    <a href={item.url || "/"} target={isExternal(item.url) ? "_blank" : undefined} rel="noreferrer">
                      Открыть
                    </a>
                  </div>
                ))}
              </div>
            </div>

            {paymentContent.note ? <div className={styles.hint}>{paymentContent.note}</div> : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
